import { useDisclosure } from '@chakra-ui/react';
import { useSelectedLocation } from '../location-management/hooks/useSelectedLocation';
import CourtPulse from './CourtPulse';
import CourtPulseExpanded from './CourtPulseExpanded';
import { useCourtPulseData } from './useCourtPulseData';

/**
 * Self-contained Court Pulse widget for the sidebar/navbar.
 * Fetches data from the selected location and renders the radial visualization.
 * Click opens the expanded modal view.
 */
export default function CourtPulseWidget({ size = 48 }: { size?: number }) {
	const { selectedLocation } = useSelectedLocation();
	const { arcs, totalOccupancy, windowStart, state } = useCourtPulseData(selectedLocation?.id);
	const { isOpen, onOpen, onClose } = useDisclosure();

	// Only an ANSWERED read draws. The three other states all used to draw, and
	// all three drew the same thing — a full ring of courts at 0% occupancy with
	// a 0% total — because `computeOccupancy` returns zeros for a court whose
	// calendar it has no data for. That is a claim about the venue, not an
	// absence of one, which is precisely what the two exemptions covering this
	// file asserted it could never make (they were right about the COURTS read
	// and silent about the calendar fan-out this widget is mostly made of).
	//
	// Absence is the right affordance rather than an error banner: this is a
	// decorative 36px strip in the navbar, nothing else on the page depends on
	// it, and hiding it is the answer `courts-never-issued-state.test.tsx`
	// already pins for the never-issued case. Note the loading arm makes the
	// widget appear LATER, not more often — it previously painted zeros on every
	// page load until the fan-out resolved, then jumped to real values.
	if (!selectedLocation || state !== 'answered' || arcs.length === 0) return null;

	return (
		<>
			<div onClick={onOpen}>
				<CourtPulse
					arcs={arcs}
					totalOccupancy={totalOccupancy}
					windowStart={windowStart}
					size={size}
					interactive
				/>
			</div>
			<CourtPulseExpanded
				isOpen={isOpen}
				onClose={onClose}
				arcs={arcs}
				totalOccupancy={totalOccupancy}
				windowStart={windowStart}
			/>
		</>
	);
}
